import type React from "react";
import { Typography, Alert } from "antd";

const { Text } = Typography;

interface ResourcePreviewProps {
  fileId: string;
  fileUrl: string;
  type?: string;
}

const ResourcePreview: React.FC<ResourcePreviewProps> = ({
  fileId,
  fileUrl,
  type,
}) => {
  if (!fileId || !fileUrl) return null;

  const extension = fileId.substring(fileId.lastIndexOf(".")).toLowerCase();

  const renderPreview = () => {
    // Verificar si fileId es un UUID (asumiendo que los UUID tienen un patrón específico)
    const isUUID = /^[0-9a-fA-F-]{36}$/.test(fileId);

    if (type === "GAME" && !isUUID) {
      // Renderizar como iframe si no es un UUID
      return (
        <div className="resource-preview-game">
          <iframe
            src={fileUrl}
            style={{
              width: "100%",
              height: "500px",
              border: "1px solid var(--bo-border-strong)",
              borderRadius: "4px",
            }}
            title="Vista previa del juego"
          />
        </div>
      );
    }

    if (
      [".jpg", ".jpeg", ".png", ".gif", ".webp", ".svg", ".bmp"].includes(
        fileId.substring(fileId.lastIndexOf(".")).toLowerCase()
      ) ||
      isUUID
    ) {
      // Renderizar como imagen si es un UUID o tiene una extensión de imagen
      return (
        <div className="resource-preview-image">
          <img
            src={fileUrl || "/placeholder.svg"}
            alt="Vista previa"
            style={{ maxWidth: "100%", maxHeight: "300px" }}
          />
        </div>
      );
    }

    if ([".mp4", ".webm", ".ogg"].includes(fileId.substring(fileId.lastIndexOf(".")).toLowerCase())) {
      return (
        <div className="resource-preview-video">
          <video
            controls
            src={fileUrl}
            style={{ maxWidth: "100%", maxHeight: "300px" }}
          >
            Tu navegador no soporta la reproducción de videos.
          </video>
        </div>
      );
    }

    if ([".mp3", ".wav", ".ogg", ".m4a"].includes(fileId.substring(fileId.lastIndexOf(".")).toLowerCase())) {
      return (
        <div className="resource-preview-audio">
          <audio controls src={fileUrl}>
            Tu navegador no soporta la reproducción de audio.
          </audio>
        </div>
      );
    }

    if (fileId.substring(fileId.lastIndexOf(".")).toLowerCase() === ".pdf") {
      return (
        <div className="resource-preview-pdf">
          <iframe
            src={`${fileUrl}#toolbar=0&navpanes=0`}
            style={{
              width: "100%",
              height: "300px",
              border: "1px solid var(--bo-border-strong)",
              borderRadius: "2px",
            }}
            title="Vista previa PDF"
          />
        </div>
      );
    }

    if (fileId.substring(fileId.lastIndexOf(".")).toLowerCase() === ".txt") {
      return (
        <div className="resource-preview-text">
          <iframe
            src={fileUrl}
            style={{
              width: "100%",
              height: "300px",
              border: "1px solid var(--bo-border-strong)",
              borderRadius: "2px",
            }}
            title="Vista previa de texto"
          />
        </div>
      );
    }

    return (
      <Alert
        message="Vista previa no disponible"
        description={
          <div>
            <Text>
              No se puede mostrar una vista previa para este tipo de archivo.
            </Text>
            <div style={{ marginTop: "8px" }}>
              <a href={fileUrl} target="_blank" rel="noopener noreferrer">
                Descargar archivo
              </a>
            </div>
          </div>
        }
        type="info"
        showIcon
      />
    );
  };

  return (
    <div className="resource-preview-container">
      <Typography.Title level={5}>Vista previa</Typography.Title>
      {renderPreview()}
    </div>
  );
};

export default ResourcePreview;
